import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getMELItems, getMELProcedures } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './DocumentsFolderPage.css';

const OPERATORS = [
  { code: 'WZZ', label: 'WZZ', name: 'Wizz Air Hungary', registrations: 'HA- / G-' },
  { code: 'WMT', label: 'WMT', name: 'Wizz Air Malta', registrations: '9H-' }
];

const DOCUMENT_TYPES = [
  { id: 'mel', label: 'MEL Items', title: 'Minimum Equipment List' },
  { id: 'procedures', label: 'Operational Procedures', title: 'MEL Operational Procedures' }
];

const PINNED_MELS_STORAGE_KEY = 'etlb_pinned_mels';
const SECTION_STRIP_DRAG_THRESHOLD = 10;

const getOperatorForRegistration = (registration) => (
  String(registration || '').trim().toUpperCase().startsWith('9H-') ? 'WMT' : 'WZZ'
);

const formatApplicability = (value) => (
  String(value || '')
    .split(/[,;|\n]+/)
    .map(item => item.trim())
    .filter(Boolean)
);

const splitInstalledRequired = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { numberInstalled: '', numberRequired: '' };

  const spacedParts = raw
    .split(/(?:\s+|\/)+/)
    .map(part => part.trim())
    .filter(Boolean);

  if (spacedParts.length >= 2) {
    return { numberInstalled: spacedParts[0], numberRequired: spacedParts[1] };
  }

  const compact = raw.replace(/[^\dA-Za-z]/g, '');
  if (!compact) return { numberInstalled: '', numberRequired: '' };
  if (!/^\d+$/.test(compact)) return { numberInstalled: compact, numberRequired: '' };
  if (compact.length === 1) return { numberInstalled: compact, numberRequired: '' };
  if (compact.length === 2) return { numberInstalled: compact.slice(0, 1), numberRequired: compact.slice(1) };
  if (compact.length === 3) return { numberInstalled: compact.slice(0, 2), numberRequired: compact.slice(2) };
  if (compact.endsWith('0')) return { numberInstalled: compact.slice(0, -1), numberRequired: compact.slice(-1) };

  const splitAt = Math.ceil(compact.length / 2);
  return { numberInstalled: compact.slice(0, splitAt), numberRequired: compact.slice(splitAt) };
};

const getMelRequirementData = (item) => {
  const fallback = splitInstalledRequired(item?.installed_required);
  return {
    repairInterval: item?.repair_interval || item?.mel_type || '-',
    numberInstalled: item?.number_installed || fallback.numberInstalled || '-',
    numberRequired: item?.number_required || fallback.numberRequired || '-',
    placard: item?.placard_required === true ? 'Yes' : item?.placard_required === false ? 'No' : '-'
  };
};

const renderMelRequirementTable = (item) => {
  const data = getMelRequirementData(item);

  return (
    <div className="documents-detail-section documents-mel-table-section">
      <span>MEL Item Limits</span>
      <div className="documents-mel-table-wrap">
        <table className="documents-mel-table">
          <thead>
            <tr>
              <th>Repair interval</th>
              <th>Nbr installed</th>
              <th>Nbr required</th>
              <th>Placard</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{data.repairInterval}</td>
              <td>{data.numberInstalled}</td>
              <td>{data.numberRequired}</td>
              <td>{data.placard}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderMelDetailInlineText = (text) => String(text || '');

const normalizePdfDisplayColor = (color) => {
  const value = String(color || '').trim().toLowerCase();
  if (!value || value === '#000000' || value === '#ffffff') return '';
  if (value === '#5f5f5f' || value === '#666666') return '#aeb7c8';
  if (value === '#ff9900') return '#ffb33f';
  return value;
};

const getPdfColorClass = (color) => {
  const value = String(color || '').trim().toLowerCase();
  if (value === '#ff9900') return 'pdf-color-orange';
  if (value === '#5f5f5f' || value === '#666666') return 'pdf-color-muted';
  if (value === '#ff0000' || value === '#c00000') return 'pdf-color-red';
  if (value === '#00b050' || value === '#008000') return 'pdf-color-green';
  if (value === '#0070c0' || value === '#0000ff') return 'pdf-color-blue';
  return '';
};

const isMarkerSpan = (text) => /^(?:\([om]\)(?:\([om]\))?|\d+\)|[-•])$/.test(String(text || '').trim());

const renderStyledPdfSpans = (spans = []) => {
  if (!Array.isArray(spans) || !spans.length) return null;

  return spans.map((span, index) => {
    const color = normalizePdfDisplayColor(span.color);
    const colorClass = getPdfColorClass(span.color);
    const nextSpan = spans[index + 1];
    const text = `${span.text || ''}${isMarkerSpan(span.text) && nextSpan?.text && !/^\s/.test(nextSpan.text) ? ' ' : ''}`;
    const style = {
      ...(color ? { color } : {}),
      ...(span.bold ? { fontWeight: 600 } : {}),
      ...(span.italic ? { fontStyle: 'italic' } : {})
    };

    return (
      <span key={`${index}-${span.text}-${span.x || 0}`} className={`documents-pdf-span ${colorClass}`} style={style}>
        {text}
      </span>
    );
  });
};

const getRichMelLineType = (line) => {
  const value = String(line || '').trim();
  if (/^Reference\(s\)$/i.test(value)) return 'references-title';
  if (/^\([om]\)\s*Refer to/i.test(value)) return 'reference';
  if (/^Note:/i.test(value)) return 'note';
  if (/^\d+\)\s*/.test(value)) return 'numbered';
  if (/^\([om]\)(?:\([om]\))?\s*/.test(value)) return 'condition';
  return 'body';
};

const renderRichDocumentBlocks = (blocks, mode = 'mel', compact = false) => {
  if (!Array.isArray(blocks) || !blocks.length) return null;

  return (
    <div className={`documents-rich-text ${mode === 'procedure' ? 'is-procedure' : 'is-mel'} ${compact ? 'compact' : ''}`}>
      {blocks.map((block, index) => {
        if (block?.type === 'table') {
          return (
            <div key={`rich-table-${index}`} className="documents-rich-table-wrap">
              <table className="documents-rich-table">
                <tbody>
                  {(block.rows || []).map((row, rowIndex) => (
                    <tr key={`${rowIndex}-${row.map(cell => cell.text).join('|')}`}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${cellIndex}-${cell.text}`}>
                          {renderStyledPdfSpans(cell.spans) || cell.text}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        const plain = block?.plain || block?.text || '';
        const type = mode === 'procedure' ? getProcedureLineType(plain) : getRichMelLineType(plain);
        const indent = Math.max(0, Math.min(96, Number(block?.indent) || 0));

        return (
          <div
            key={`rich-line-${index}-${plain.slice(0, 24)}`}
            className={`documents-rich-line type-${type}`}
            style={{ paddingLeft: `${indent}px` }}
          >
            {renderStyledPdfSpans(block.spans) || block.text}
          </div>
        );
      })}
    </div>
  );
};

const getMelDetailLineType = (line) => {
  const value = String(line || '').trim();
  if (/^Reference\(s\)$/i.test(value)) return 'references-title';
  if (/^\([om]\)\s+Refer to/i.test(value)) return 'reference';
  if (/^Note:/i.test(value)) return 'note';
  if (/^\d+\)\s+/.test(value)) return 'numbered';
  if (/^\([om]\)(?:\([om]\))?\s+/.test(value)) return 'condition';
  return 'body';
};

const renderMelDetailLine = (line, index) => {
  const value = String(line || '').trim();
  const type = getMelDetailLineType(value);

  if (type === 'references-title') {
    return <div key={`${index}-${value}`} className="documents-mel-detail-line type-references-title"><span>{value}</span></div>;
  }

  const markerMatch = value.match(/^(\([om]\)(?:\([om]\))?|\d+\))\s*(.*)$/i);
  if (markerMatch) {
    return (
      <div key={`${index}-${value}`} className={`documents-mel-detail-line type-${type}`}>
        <span className="documents-mel-detail-marker">{markerMatch[1]}</span>
        <span>{renderMelDetailInlineText(markerMatch[2])}</span>
      </div>
    );
  }

  return (
    <div key={`${index}-${value}`} className={`documents-mel-detail-line type-${type}`}>
      {renderMelDetailInlineText(value)}
    </div>
  );
};

const renderMelDetailText = (text, richBlocks = []) => {
  const richContent = renderRichDocumentBlocks(richBlocks, 'mel');
  if (richContent) return richContent;

  const lines = String(text || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  if (!lines.length) return <p>No details available.</p>;

  return (
    <div className="documents-mel-detail-text">
      {lines.map((line, index) => renderMelDetailLine(line, index))}
    </div>
  );
};

const isAllApplicability = (items) => (
  !items.length || (items.length === 1 && /^ALL$/i.test(items[0]))
);

const renderApplicabilitySection = (items, label) => {
  const allAircraft = isAllApplicability(items);

  return (
    <div className="documents-detail-section">
      <span>{label}</span>
      {allAircraft ? (
        <p>ALL</p>
      ) : (
        <details className="documents-applicability-dropdown">
          <summary>
            <span>{items.length} aircraft</span>
            <span className="documents-applicability-summary-action">Show list</span>
          </summary>
          <div className="documents-applicability-list">
            {items.map(item => <span key={item} className="documents-applicability-chip">{item}</span>)}
          </div>
        </details>
      )}
    </div>
  );
};

const getRecordKey = (item) => [
  item.operator_code || 'WZZ',
  item.reference || '',
  item.applicability || '',
  item.title || ''
].join('|');

const getPinnedId = ({ documentType, operatorCode, recordKey }) => (
  [documentType || 'mel', operatorCode || 'WZZ', recordKey || ''].join('|')
);

const loadPinnedMels = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PINNED_MELS_STORAGE_KEY) || '[]');
    return Array.isArray(parsed)
      ? parsed.filter(item => item?.id && item?.recordKey && item?.documentType && item?.operatorCode)
      : [];
  } catch {
    return [];
  }
};

const savePinnedMels = (pins) => {
  try {
    localStorage.setItem(PINNED_MELS_STORAGE_KEY, JSON.stringify(pins));
  } catch {
    // Pins are a convenience; ignore storage failures.
  }
};

const sortRecords = (items) => [...items].sort((a, b) => (
  String(a.reference || '').localeCompare(String(b.reference || ''), undefined, { numeric: true })
));

const buildChapters = (items) => {
  const chapterMap = new Map();

  items.forEach(item => {
    const chapterCode = item.ata_chapter || '00';
    const sectionCode = item.ata_section || chapterCode;

    if (!chapterMap.has(chapterCode)) {
      chapterMap.set(chapterCode, {
        code: chapterCode,
        title: item.ata_chapter_title || `ATA ${chapterCode}`,
        count: 0,
        sections: new Map()
      });
    }

    const chapter = chapterMap.get(chapterCode);
    chapter.count += 1;

    if (!chapter.sections.has(sectionCode)) {
      chapter.sections.set(sectionCode, {
        code: sectionCode,
        title: item.ata_section_title || sectionCode,
        count: 0
      });
    }
    chapter.sections.get(sectionCode).count += 1;
  });

  return [...chapterMap.values()]
    .map(chapter => ({
      ...chapter,
      sections: [...chapter.sections.values()].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
    }))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
};

const recordMatchesSearch = (item, query) => {
  if (!query) return true;
  return [
    item.reference,
    item.title,
    item.base_title,
    item.ata_chapter_title,
    item.ata_section_title,
    item.applicability,
    item.remarks,
    item.procedure_text,
    item.search_text
  ].filter(Boolean).join(' ').toLowerCase().includes(query);
};

const procedureActionEndStatePattern = /\b(?:OFF|ON|PULL|PUSH|AUTO|OVRD|OPEN|CLOSE|PRESS|RQRD|USE|SHUT|HI|NORM|MAN|CHECK|ATT|MCT|IDLE|REQUIRED|INOPERATIVE|FULL|TEST|DN|ALTN|SET|CENTERED|ZERO|ARMED|RED|PLACE|RELEASE)\s*$/i;

const isProcedureBulletPrefix = (value) =>
  /^(?:-|\.|\u2010|\u2011|\u2012|\u2013|\u2022)\s*/.test(value);

const stripProcedureBulletPrefix = (value) =>
  value.replace(/^(?:-|\.|\u2010|\u2011|\u2012|\u2013|\u2022)\s*/, '');

const isProcedureActionLine = (value) => {
  const clean = stripProcedureBulletPrefix(value);
  return (
    /\.{4,}/.test(clean) ||
    procedureActionEndStatePattern.test(clean) ||
    /\b(?:pb-sw|C\/B|knob|switch|lever|selector|valve|button|handle|guard)\b/i.test(clean) ||
    /\b(?:OFF|ON|PULL|PUSH|SET|CHECK|DISREGARD|CLEAR|CONFIRM|APPLY)\b/i.test(clean)
  );
};

const getProcedureLineType = (line) => {
  const value = String(line || '').trim();
  if (!value) return 'blank';
  if (/^note:/i.test(value)) return 'note';
  if (/^\|.+\|$/.test(value)) return 'table';
  if (/^(general information|flight preparation\/limitations|during cockpit preparation|after engine start|before takeoff|for takeoff|in flight|for landing|after landing|before leaving the aircraft)$/i.test(value)) {
    return 'heading';
  }
  if (/^[A-Z0-9 /()&.-]{4,}$/.test(value) && /[A-Z]/.test(value) && value.length <= 48) return 'heading';
  if (isProcedureActionLine(value)) return 'action';
  if (isProcedureBulletPrefix(value)) return 'check';
  return 'body';
};

const parseProcedureTableRow = (line) => String(line || '')
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map(cell => cell.trim());

const buildProcedureBlocks = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  const blocks = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (getProcedureLineType(line) === 'table') {
      const rows = [];
      while (index < lines.length && getProcedureLineType(lines[index]) === 'table') {
        rows.push(parseProcedureTableRow(lines[index]));
        index += 1;
      }
      index -= 1;
      blocks.push({ type: 'table', rows });
    } else {
      blocks.push({ type: 'line', line, lineType: getProcedureLineType(line) });
    }
  }

  return blocks;
};

const renderProcedureInlineText = (text) => String(text || '');

const renderProcedureText = (text, compact = false, richBlocks = []) => {
  const richContent = renderRichDocumentBlocks(richBlocks, 'procedure', compact);
  if (richContent) return richContent;

  return (
    <div className={`documents-procedure-text ${compact ? 'compact' : ''}`}>
      {buildProcedureBlocks(text)
        .map((block, index) => {
          if (block.type === 'table') {
            return (
              <div key={`table-${index}`} className="documents-procedure-table-wrap">
                <table className="documents-procedure-table">
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={`${rowIndex}-${row.join('|')}`}>
                        {row.map((cell, cellIndex) => (
                          <td key={`${cellIndex}-${cell}`}>{renderProcedureInlineText(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }

          const { line, lineType: type } = block;
          return (
            <div key={`${index}-${line.slice(0, 24)}`} className={`documents-procedure-line type-${type}`}>
              {renderProcedureInlineText(line)}
            </div>
          );
        })}
    </div>
  );
};

const DocumentsFolderPage = () => {
  const { aircraft } = useAuth();
  const [activeOperator, setActiveOperator] = useState(() => getOperatorForRegistration(aircraft?.registration));
  const [documentType, setDocumentType] = useState('mel');
  const [itemsByOperator, setItemsByOperator] = useState({ WZZ: [], WMT: [] });
  const [proceduresByOperator, setProceduresByOperator] = useState({ WZZ: [], WMT: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedRecordKey, setSelectedRecordKey] = useState('');
  const [readerOpen, setReaderOpen] = useState(false);
  const [pinnedMels, setPinnedMels] = useState(loadPinnedMels);
  const [pinnedViewOpen, setPinnedViewOpen] = useState(false);
  const [selectedPinnedId, setSelectedPinnedId] = useState('');
  const [sectionStripDragging, setSectionStripDragging] = useState(false);
  const sectionStripDragRef = useRef({ active: false, moved: false, startX: 0, scrollLeft: 0 });
  const suppressSectionClickRef = useRef(false);

  useEffect(() => {
    setActiveOperator(getOperatorForRegistration(aircraft?.registration));
  }, [aircraft?.registration]);

  useEffect(() => {
    savePinnedMels(pinnedMels);
  }, [pinnedMels]);

  useEffect(() => {
    let cancelled = false;

    const loadDocuments = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const [wzzItemsRes, wmtItemsRes, wzzProceduresRes, wmtProceduresRes] = await Promise.all([
          getMELItems({ operator: 'WZZ', limit: 2000 }),
          getMELItems({ operator: 'WMT', limit: 2000 }),
          getMELProcedures({ operator: 'WZZ', limit: 2000 }),
          getMELProcedures({ operator: 'WMT', limit: 2000 })
        ]);

        if (cancelled) return;
        setItemsByOperator({
          WZZ: sortRecords(wzzItemsRes.data.items || []),
          WMT: sortRecords(wmtItemsRes.data.items || [])
        });
        setProceduresByOperator({
          WZZ: sortRecords(wzzProceduresRes.data.procedures || []),
          WMT: sortRecords(wmtProceduresRes.data.procedures || [])
        });
      } catch (err) {
        try {
          const fallbackRes = await fetch(`${process.env.PUBLIC_URL || ''}/mel_items.json`);
          if (!fallbackRes.ok) throw new Error(`MEL fallback failed: ${fallbackRes.status}`);
          const fallbackData = await fallbackRes.json();
          const items = fallbackData.items || [];
          const procedures = fallbackData.procedures || [];

          if (cancelled) return;
          setItemsByOperator({
            WZZ: sortRecords(items.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WZZ')),
            WMT: sortRecords(items.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WMT'))
          });
          setProceduresByOperator({
            WZZ: sortRecords(procedures.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WZZ')),
            WMT: sortRecords(procedures.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WMT'))
          });
        } catch (fallbackErr) {
          if (!cancelled) {
            setItemsByOperator({ WZZ: [], WMT: [] });
            setProceduresByOperator({ WZZ: [], WMT: [] });
            setLoadError('MEL documents unavailable');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDocuments();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeRecords = useMemo(() => (
    documentType === 'procedures'
      ? proceduresByOperator[activeOperator] || []
      : itemsByOperator[activeOperator] || []
  ), [activeOperator, documentType, itemsByOperator, proceduresByOperator]);
  const activeOperatorMeta = OPERATORS.find(operator => operator.code === activeOperator) || OPERATORS[0];
  const activeDocumentMeta = DOCUMENT_TYPES.find(type => type.id === documentType) || DOCUMENT_TYPES[0];
  const chapters = useMemo(() => buildChapters(activeRecords), [activeRecords]);
  const selectedChapterNode = chapters.find(chapter => chapter.code === selectedChapter) || null;
  const selectedSectionNode = selectedChapterNode?.sections.find(section => section.code === selectedSection) || null;
  const searchQuery = search.trim().toLowerCase();

  const visibleRecords = useMemo(() => {
    let nextItems = activeRecords;

    if (searchQuery) {
      nextItems = activeRecords.filter(item => recordMatchesSearch(item, searchQuery));
    } else if (selectedSection) {
      nextItems = activeRecords.filter(item => item.ata_section === selectedSection);
    } else if (selectedChapter) {
      nextItems = activeRecords.filter(item => item.ata_chapter === selectedChapter);
    }

    return sortRecords(nextItems);
  }, [activeRecords, searchQuery, selectedChapter, selectedSection]);

  const selectedRecord = useMemo(() => (
    visibleRecords.find(item => getRecordKey(item) === selectedRecordKey)
    || activeRecords.find(item => getRecordKey(item) === selectedRecordKey)
    || null
  ), [activeRecords, selectedRecordKey, visibleRecords]);
  const pinnedRecords = useMemo(() => (
    pinnedMels
      .map(pin => {
        const sourceRecords = pin.documentType === 'procedures'
          ? proceduresByOperator[pin.operatorCode] || []
          : itemsByOperator[pin.operatorCode] || [];
        const record = sourceRecords.find(item => getRecordKey(item) === pin.recordKey) || null;
        return {
          ...pin,
          record,
          reference: record?.reference || pin.reference,
          title: record?.title || record?.base_title || pin.title,
          subtitle: record
            ? pin.documentType === 'procedures'
              ? `Procedure - ${record.ata_section || ''}`
              : `MEL ${record.mel_type || '-'} - ${record.ata_section || ''}`
            : pin.subtitle || `${pin.documentType === 'procedures' ? 'Procedure' : 'MEL Item'}`
        };
      })
      .sort((a, b) => String(b.pinnedAt || '').localeCompare(String(a.pinnedAt || '')))
  ), [itemsByOperator, pinnedMels, proceduresByOperator]);
  const pinnedIdSet = useMemo(() => new Set(pinnedMels.map(pin => pin.id)), [pinnedMels]);
  const selectedRecordPinId = selectedRecord
    ? getPinnedId({
        documentType,
        operatorCode: selectedRecord.operator_code || activeOperator,
        recordKey: getRecordKey(selectedRecord)
      })
    : '';
  const selectedRecordPinned = selectedRecordPinId ? pinnedIdSet.has(selectedRecordPinId) : false;
  const selectedPinnedRecord = pinnedRecords.find(pin => pin.id === selectedPinnedId) || null;
  const selectedPinnedDetailApplicability = formatApplicability(selectedPinnedRecord?.record?.applicability);

  useEffect(() => {
    if (loading) return;
    if (!chapters.length) return;
    if (!selectedChapter) setSelectedChapter(chapters[0].code);
  }, [chapters, loading, selectedChapter]);

  useEffect(() => {
    if (!selectedChapterNode) {
      if (selectedSection) setSelectedSection('');
      return;
    }
    if (selectedSection && !selectedChapterNode.sections.some(section => section.code === selectedSection)) {
      setSelectedSection('');
    }
  }, [selectedChapterNode, selectedSection]);

  useEffect(() => {
    if (!visibleRecords.length) {
      if (selectedRecordKey) setSelectedRecordKey('');
      return;
    }

    if (!visibleRecords.some(item => getRecordKey(item) === selectedRecordKey)) {
      setSelectedRecordKey(getRecordKey(visibleRecords[0]));
    }
  }, [selectedRecordKey, visibleRecords]);

  const resetDocumentPosition = () => {
    setSearch('');
    setSelectedChapter('');
    setSelectedSection('');
    setSelectedRecordKey('');
    setReaderOpen(false);
  };

  const handleOperatorChange = (operatorCode) => {
    setActiveOperator(operatorCode);
    resetDocumentPosition();
  };

  const handlePinnedOpen = (pin) => {
    if (!pin.record) return;
    setSelectedPinnedId(pin.id);
  };

  const handleRemovePinned = (event, pinId) => {
    event.stopPropagation();
    setPinnedMels(current => current.filter(pin => pin.id !== pinId));
    if (selectedPinnedId === pinId) setSelectedPinnedId('');
  };

  const handleToggleSelectedPin = () => {
    if (!selectedRecord) return;

    const operatorCode = selectedRecord.operator_code || activeOperator;
    const recordKey = getRecordKey(selectedRecord);
    const id = getPinnedId({ documentType, operatorCode, recordKey });

    setPinnedMels(current => {
      if (current.some(pin => pin.id === id)) {
        return current.filter(pin => pin.id !== id);
      }

      const isProcedure = documentType === 'procedures';
      return [
        {
          id,
          documentType,
          operatorCode,
          recordKey,
          reference: selectedRecord.reference || '',
          title: selectedRecord.title || selectedRecord.base_title || '',
          subtitle: isProcedure
            ? `Procedure - ${selectedRecord.ata_section || ''}`
            : `MEL ${selectedRecord.mel_type || '-'} - ${selectedRecord.ata_section || ''}`,
          pinnedAt: new Date().toISOString()
        },
        ...current
      ];
    });
  };

  const handleDocumentTypeChange = (nextType) => {
    setDocumentType(nextType);
    resetDocumentPosition();
  };

  const handleChapterSelect = (chapterCode) => {
    setSearch('');
    setSelectedChapter(chapterCode);
    setSelectedSection('');
  };

  const handleSectionSelect = (sectionCode) => {
    if (suppressSectionClickRef.current) {
      suppressSectionClickRef.current = false;
      return;
    }
    setSearch('');
    setSelectedSection(sectionCode);
  };

  const handleSectionStripWheel = (event) => {
    if (!event.currentTarget) return;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!delta) return;
    event.currentTarget.scrollLeft += delta;
  };

  const handleSectionStripPointerDown = (event) => {
    if (event.button !== 0) return;
    sectionStripDragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      scrollLeft: event.currentTarget.scrollLeft
    };
    setSectionStripDragging(false);
  };

  const handleSectionStripPointerMove = (event) => {
    const drag = sectionStripDragRef.current;
    if (!drag.active) return;

    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) < SECTION_STRIP_DRAG_THRESHOLD) return;

    drag.moved = true;
    setSectionStripDragging(true);
    event.currentTarget.scrollLeft = drag.scrollLeft - delta;
    event.preventDefault();
  };

  const finishSectionStripDrag = (event) => {
    const drag = sectionStripDragRef.current;
    if (!drag.active) return;

    if (drag.moved) {
      suppressSectionClickRef.current = true;
      window.setTimeout(() => {
        suppressSectionClickRef.current = false;
      }, 0);
    }

    sectionStripDragRef.current = { active: false, moved: false, startX: 0, scrollLeft: 0 };
    setSectionStripDragging(false);
  };

  const detailApplicability = formatApplicability(selectedRecord?.applicability);
  const isProcedureMode = documentType === 'procedures';
  const indexTitle = searchQuery
    ? 'Search Results'
    : selectedSectionNode
      ? `${selectedSectionNode.code} ${selectedSectionNode.title}`
        : selectedChapterNode
        ? `${selectedChapterNode.code} ${selectedChapterNode.title}`
        : activeDocumentMeta.title;

  return (
    <div className="documents-page">
      <header className="documents-header">
        <div className="documents-heading">
          <div className="documents-title">Documents Folder</div>
          <div className="documents-subtitle">{activeDocumentMeta.title}</div>
        </div>
        <div className="documents-header-controls">
          <div className="documents-type-toggle" role="tablist" aria-label="Document type">
            {DOCUMENT_TYPES.map(type => (
              <button
                key={type.id}
                type="button"
                role="tab"
                aria-selected={documentType === type.id}
                className={`documents-type-btn ${documentType === type.id ? 'active' : ''}`}
                onClick={() => handleDocumentTypeChange(type.id)}
              >
                {type.label}
              </button>
            ))}
          </div>
          <div className="documents-pinned-wrap">
            <button
              type="button"
              className={`documents-pinned-trigger ${pinnedViewOpen ? 'active' : ''}`}
              onClick={() => {
                setSelectedPinnedId('');
                setPinnedViewOpen(true);
              }}
            >
              <span>Pinned MELs</span>
              <small>{pinnedRecords.length}</small>
            </button>
          </div>
        </div>
      </header>

      <div className="documents-body">
        <aside className="documents-sidebar">
          <div className="documents-panel-title">Documents</div>
          <div className="documents-library-list">
            {OPERATORS.map(operator => {
              const melCount = (itemsByOperator[operator.code] || []).length;
              const procedureCount = (proceduresByOperator[operator.code] || []).length;
              return (
                <button
                  key={operator.code}
                  type="button"
                  className={`documents-library-item ${activeOperator === operator.code ? 'active' : ''}`}
                  onClick={() => handleOperatorChange(operator.code)}
                >
                  <span className="documents-library-code">{operator.label} MEL</span>
                  <span className="documents-library-name">{operator.name}</span>
                  <span className="documents-library-meta">{operator.registrations} - {melCount} items - {procedureCount} procedures</span>
                </button>
              );
            })}
          </div>

          <div className="documents-panel-divider" />
          <div className="documents-panel-title">ATA Contents</div>
          <div className="documents-chapter-list">
            {loading ? (
              <div className="documents-empty">Loading...</div>
            ) : chapters.map(chapter => (
              <button
                key={chapter.code}
                type="button"
                className={`documents-chapter-item ${selectedChapter === chapter.code && !searchQuery ? 'active' : ''}`}
                onClick={() => handleChapterSelect(chapter.code)}
              >
                <span className="documents-chapter-code">{chapter.code}</span>
                <span className="documents-chapter-title">{chapter.title}</span>
                <span className="documents-chapter-count">{chapter.count}</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="documents-index-panel">
          <div className="documents-search-row">
            <input
              className="documents-search-input"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={isProcedureMode ? 'Search procedures...' : 'Search MEL...'}
            />
          </div>

          <div className="documents-index-head">
            <div>
              <span className="documents-index-title">{indexTitle}</span>
              <span className="documents-index-operator">{activeOperatorMeta.name} - {activeDocumentMeta.label}</span>
            </div>
            <span className="documents-count-tag">{visibleRecords.length} {isProcedureMode ? 'procedures' : 'items'}</span>
          </div>

          {!searchQuery && selectedChapterNode?.sections.length ? (
            <div
              className={`documents-section-strip ${sectionStripDragging ? 'dragging' : ''}`}
              onWheel={handleSectionStripWheel}
              onPointerDown={handleSectionStripPointerDown}
              onPointerMove={handleSectionStripPointerMove}
              onPointerUp={finishSectionStripDrag}
              onPointerCancel={finishSectionStripDrag}
              onPointerLeave={finishSectionStripDrag}
            >
              <button
                type="button"
                className={`documents-section-chip ${!selectedSection ? 'active' : ''}`}
                onClick={() => handleSectionSelect('')}
              >
                All
              </button>
              {selectedChapterNode.sections.map(section => (
                <button
                  key={section.code}
                  type="button"
                  className={`documents-section-chip ${selectedSection === section.code ? 'active' : ''}`}
                  onClick={() => handleSectionSelect(section.code)}
                >
                  {section.code}
                </button>
              ))}
            </div>
          ) : null}

          <div className="documents-item-list">
            {loading ? (
              <div className="documents-empty">Loading...</div>
            ) : loadError ? (
              <div className="documents-empty">{loadError}</div>
            ) : !visibleRecords.length ? (
              <div className="documents-empty">No documents found.</div>
            ) : visibleRecords.map(item => {
              const itemRecordKey = getRecordKey(item);
              const itemPinned = pinnedIdSet.has(getPinnedId({
                documentType,
                operatorCode: item.operator_code || activeOperator,
                recordKey: itemRecordKey
              }));

              return (
                <button
                  key={itemRecordKey}
                  type="button"
                  className={`documents-item ${selectedRecord && getRecordKey(selectedRecord) === itemRecordKey ? 'active' : ''}`}
                  onClick={() => setSelectedRecordKey(itemRecordKey)}
                >
                  <span className="documents-item-ref">{item.reference}</span>
                  <span className="documents-item-title">{item.title || item.base_title}</span>
                  <span className="documents-item-meta">
                    <span>{isProcedureMode ? 'Procedure' : `MEL ${item.mel_type || '-'}`}</span>
                    <span>{item.ata_section}</span>
                    <span>{isProcedureMode ? item.ata_section_title || 'Procedure' : item.placard_required ? 'Placard' : 'No placard'}</span>
                    {itemPinned ? <span className="documents-item-pinned">Pinned</span> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className={`documents-detail-panel ${isProcedureMode ? 'is-procedure' : ''}`}>
          {selectedRecord ? (
            <>
              <div className="documents-detail-head">
                <div>
                  <div className="documents-detail-reference">{selectedRecord.reference}</div>
                  <div className="documents-detail-title">{selectedRecord.title || selectedRecord.base_title}</div>
                </div>
                <div className="documents-detail-actions">
                  <button
                    type="button"
                    className={`documents-pin-btn ${selectedRecordPinned ? 'active' : ''}`}
                    onClick={handleToggleSelectedPin}
                  >
                    {selectedRecordPinned ? 'Pinned' : 'Pin'}
                  </button>
                  <button type="button" className="documents-reader-btn" onClick={() => setReaderOpen(true)}>Full Screen</button>
                </div>
              </div>

              {isProcedureMode ? (
                <>
                  {renderApplicabilitySection(detailApplicability, 'Applicable to')}

                  <div className="documents-detail-section documents-procedure-document-section">
                    {renderProcedureText(selectedRecord.procedure_text, false, selectedRecord.procedure_blocks)}
                  </div>
                </>
              ) : (
                <>
                  <div className="documents-detail-grid">
                    <div className="documents-detail-pair">
                      <span>ATA</span>
                      <strong>{selectedRecord.ata_chapter} {selectedRecord.ata_chapter_title}</strong>
                    </div>
                    <div className="documents-detail-pair">
                      <span>Section</span>
                      <strong>{selectedRecord.ata_section} {selectedRecord.ata_section_title}</strong>
                    </div>
                  </div>

                  {renderApplicabilitySection(detailApplicability, 'Applicability')}

                  {renderMelRequirementTable(selectedRecord)}

                  <div className="documents-detail-section">
                    <span>Details</span>
                    {renderMelDetailText(selectedRecord.remarks, selectedRecord.remarks_blocks)}
                  </div>
                </>
              )}

            </>
          ) : (
            <div className="documents-empty documents-detail-empty">
              {loading ? 'Loading...' : 'No document selected.'}
            </div>
          )}
        </section>
      </div>

      {readerOpen && selectedRecord ? (
        <div className="documents-reader-overlay">
          <div className="documents-reader-shell">
            <header className="documents-reader-header">
              <div>
                <div className="documents-reader-reference">{selectedRecord.reference}</div>
                <div className="documents-reader-title">{selectedRecord.title}</div>
              </div>
              <button type="button" className="documents-reader-close" onClick={() => setReaderOpen(false)}>Close</button>
            </header>
            <div className="documents-reader-meta">
              <span>{activeOperatorMeta.name}</span>
            </div>
            <div className="documents-reader-content">
              {isProcedureMode ? (
                <>
                  {renderApplicabilitySection(detailApplicability, 'Applicable to')}
                  {renderProcedureText(selectedRecord.procedure_text, true, selectedRecord.procedure_blocks)}
                </>
              ) : (
                <div className="documents-reader-mel-item">
                  <div className="documents-detail-grid">
                    <div className="documents-detail-pair">
                      <span>ATA</span>
                      <strong>{selectedRecord.ata_chapter} {selectedRecord.ata_chapter_title}</strong>
                    </div>
                    <div className="documents-detail-pair">
                      <span>Section</span>
                      <strong>{selectedRecord.ata_section} {selectedRecord.ata_section_title}</strong>
                    </div>
                  </div>
                  {renderApplicabilitySection(detailApplicability, 'Applicability')}
                  {renderMelRequirementTable(selectedRecord)}
                  <div className="documents-detail-section">
                    <span>Details</span>
                    {renderMelDetailText(selectedRecord.remarks, selectedRecord.remarks_blocks)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {pinnedViewOpen ? (
        <div className="documents-pinned-overlay">
          <div className="documents-pinned-screen">
            <header className="documents-pinned-screen-header">
              <div>
                <div className="documents-pinned-screen-title">
                  {selectedPinnedRecord?.record ? selectedPinnedRecord.reference : 'Pinned MELs'}
                </div>
                <div className="documents-pinned-screen-subtitle">
                  {selectedPinnedRecord?.record
                    ? selectedPinnedRecord.title || selectedPinnedRecord.subtitle
                    : `${pinnedRecords.length} saved`}
                </div>
              </div>
              <div className="documents-pinned-screen-actions">
                {selectedPinnedRecord?.record ? (
                  <button type="button" className="documents-reader-close" onClick={() => setSelectedPinnedId('')}>Back</button>
                ) : (
                  <button
                    type="button"
                    className="documents-reader-close"
                    onClick={() => {
                      setPinnedViewOpen(false);
                      setSelectedPinnedId('');
                    }}
                  >
                    Close
                  </button>
                )}
              </div>
            </header>

            <div className="documents-pinned-screen-content">
              {selectedPinnedRecord?.record ? (
                <div className="documents-pinned-reader">
                  {selectedPinnedRecord.documentType === 'procedures' ? (
                    <>
                      {renderApplicabilitySection(selectedPinnedDetailApplicability, 'Applicable to')}
                      <div className="documents-detail-section documents-procedure-document-section">
                      {renderProcedureText(selectedPinnedRecord.record.procedure_text, true, selectedPinnedRecord.record.procedure_blocks)}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="documents-detail-grid">
                        <div className="documents-detail-pair">
                          <span>ATA</span>
                          <strong>{selectedPinnedRecord.record.ata_chapter} {selectedPinnedRecord.record.ata_chapter_title}</strong>
                        </div>
                        <div className="documents-detail-pair">
                          <span>Section</span>
                          <strong>{selectedPinnedRecord.record.ata_section} {selectedPinnedRecord.record.ata_section_title}</strong>
                        </div>
                      </div>
                      {renderApplicabilitySection(selectedPinnedDetailApplicability, 'Applicability')}
                      {renderMelRequirementTable(selectedPinnedRecord.record)}
                      <div className="documents-detail-section">
                        <span>Details</span>
                        {renderMelDetailText(selectedPinnedRecord.record.remarks, selectedPinnedRecord.record.remarks_blocks)}
                      </div>
                    </>
                  )}
                </div>
              ) : pinnedRecords.length ? (
                <div className="documents-pinned-grid">
                  {pinnedRecords.map(pin => (
                    <article key={pin.id} className={`documents-pinned-card ${pin.record ? '' : 'missing'}`}>
                      <button
                        type="button"
                        className="documents-pinned-card-main"
                        onClick={() => handlePinnedOpen(pin)}
                        disabled={!pin.record}
                      >
                        <span className="documents-pinned-ref">{pin.reference || 'Pinned MEL'}</span>
                        <span className="documents-pinned-title">{pin.title || 'No title'}</span>
                        <span className="documents-pinned-meta">{pin.record ? pin.subtitle : 'Not found in current MEL data'}</span>
                      </button>
                      <button
                        type="button"
                        className="documents-pinned-remove"
                        onClick={event => handleRemovePinned(event, pin.id)}
                      >
                        Unpin
                      </button>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="documents-pinned-empty documents-pinned-screen-empty">
                  Select a MEL item or procedure, then pin it from the detail header.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DocumentsFolderPage;
